<template>
  <!-- Modal de video agnóstico: soporta JWPlayer y YouTube. Compartido por Etapa 1 (SidebarHighlight) y Etapa 5 (Help & Support). -->
  <transition name="modal-fade">
    <div class="video-modal" v-if="show" @click="close">
      <div class="modal-content" @click.stop>
        <button class="close-button" @click="close" aria-label="Close video">
          <svg width="48" height="48" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M35.5833 66C51.9218 66 65.1667 52.5685 65.1667 36C65.1667 19.4315 51.9218 6 35.5833 6C19.2449 6 6 19.4315 6 36C6 52.5685 19.2449 66 35.5833 66Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M44.4792 27L26.6875 45" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M26.6875 27L44.4792 45" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div class="video-container">
          <!-- JWPlayer: reutiliza el patrón global window.jwplayer (ver VideoWrapper.vue) -->
          <div v-if="type === 'jwplayer'" :id="jwPlayerId" ref="jwPlayerContainer"></div>
          <!-- YouTube: iframe embed, sin dependencias extra -->
          <iframe
            v-else-if="type === 'youtube'"
            :src="youtubeSrc"
            :title="title || 'Video'"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
          ></iframe>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { fetchMp4Sources } from '../helpers/jwHelpers';

export default {
  name: 'VideoModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'jwplayer',
      validator: (value) => ['jwplayer', 'youtube'].includes(value)
    },
    // Vacío = sin video (el padre monta el modal permanente y solo alterna `show`).
    videoId: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      jwPlayerInstance: null,
      // Fuentes MP4 del media (se precargan al montar para evitar hls.js y permitir autostart).
      mp4Sources: null,
      // videoId al que pertenecen las fuentes cacheadas (el modal es permanente y el video cambia).
      mp4SourcesId: ''
    };
  },
  computed: {
    jwPlayerId() {
      return `jwplayer-modal-${this.videoId}`;
    },
    youtubeSrc() {
      return `https://www.youtube.com/embed/${this.videoId}?autoplay=1&rel=0`;
    }
  },
  watch: {
    show(isOpen) {
      if (isOpen) {
        this.openPlayer();
      } else {
        this.teardown();
      }
    },
    videoId() {
      // Con el modal cerrado, adelantar las fuentes del próximo video; si está abierto las
      // trae initJWPlayer (el cache se invalida solo, por mp4SourcesId).
      if (!this.show) this.prefetchSources();
    }
  },
  created() {
    // No reactivo a propósito: solo lo leen los callbacks async para saber si seguimos vivos.
    this.alive = true;
  },
  mounted() {
    // Portal al <body>: el modal es un overlay full-screen y sus ancestros pueden crear
    // stacking contexts (ej. el sidebar `.bg-image` fixed) que atraparían su z-index.
    // Moviéndolo al body escapa de esos contextos y queda siempre por encima de todo.
    // Funciona porque `.video-modal` tiene estilos globales (no scoped).
    //
    // IMPORTANTE: el padre debe montar este componente de forma PERMANENTE y abrirlo con
    // `:show`, NUNCA con `v-if`. Al mudar el $el, el ancla que Vue guarda en el padre queda
    // fuera de su lista de hijos y su insert() interno (que exige ref.parentNode === parent)
    // deja de insertar en silencio: el modal no vuelve a entrar al DOM y no hay error.
    if (this.$el && this.$el.parentNode) {
      document.body.appendChild(this.$el);
    }
    if (this.show) {
      // Si el padre nos monta ya abierto, el watcher de `show` no dispara: arrancamos acá.
      this.openPlayer();
    } else {
      // Precargar las fuentes MP4 para que el setup sea sincrónico (autostart dentro del gesto).
      this.prefetchSources();
    }
  },
  methods: {
    openPlayer() {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.onEscape);
      if (this.type === 'jwplayer') {
        // Esperar a que el contenedor exista en el DOM antes del setup
        this.$nextTick(() => this.initJWPlayer());
      }
    },
    async prefetchSources() {
      if (this.type !== 'jwplayer' || !this.videoId) return;
      const id = this.videoId;
      const sources = await fetchMp4Sources(id);
      // Descartar si el video cambió mientras se resolvía el fetch.
      if (this.videoId !== id) return;
      this.mp4Sources = sources;
      this.mp4SourcesId = id;
    },
    async initJWPlayer() {
      this.destroyJWPlayer();
      if (typeof window.jwplayer === 'undefined') {
        console.error('JWPlayer library not loaded');
        return;
      }
      // Usar las fuentes precargadas solo si son de este video; si no, traerlas ahora.
      let sources = this.mp4SourcesId === this.videoId ? this.mp4Sources : null;
      if (!sources) {
        sources = await fetchMp4Sources(this.videoId);
        this.mp4Sources = sources;
        this.mp4SourcesId = this.videoId;
      }
      // El modal pudo cerrarse mientras se traían las fuentes. Además de `show`, miramos
      // `alive` y que el contenedor siga en el DOM (si no está, jwplayer() devuelve un stub
      // sin setup()).
      if (!this.alive || !this.show) return;
      if (!document.getElementById(this.jwPlayerId)) return;

      const config = {
        width: '100%',
        aspectratio: '16:9',
        autostart: true,
        // El JWPlayer self-hosted (8.3.32) trae un hls.js viejo que falla al bufferear
        // ciertos streams HLS (JW Warning 334002); por eso preferimos MP4 progresivo.
        hlsjsdefault: false,
        playlist: sources && sources.length
          ? [{ sources }]
          : `https://cdn.jwplayer.com/v2/media/${this.videoId}`
      };
      const player = window.jwplayer(this.jwPlayerId);
      if (!player || typeof player.setup !== 'function') return;
      this.jwPlayerInstance = player.setup(config);
    },
    destroyJWPlayer() {
      if (this.jwPlayerInstance) {
        try {
          this.jwPlayerInstance.remove();
        } catch (e) {
          console.warn('Error destroying JWPlayer instance:', e);
        }
        this.jwPlayerInstance = null;
      }
    },
    teardown() {
      // El iframe de YouTube se destruye solo al desmontarse (v-if); JWPlayer requiere remove() explícito
      this.destroyJWPlayer();
      document.removeEventListener('keydown', this.onEscape);
      document.body.style.overflow = '';
    },
    // ESC cierra el modal; en fullscreen no, porque ahí el navegador usa ESC para salir.
    onEscape(e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      if (document.fullscreenElement || document.webkitFullscreenElement) return;
      this.close();
    },
    close() {
      this.$emit('close');
    }
  },
  beforeDestroy() {
    this.alive = false;
    this.teardown();
    // Limpieza del nodo portado al body (evita nodos huérfanos si Vue no lo alcanza).
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  }
};
</script>
